import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  Users,
  Globe,
  Heart,
  Target,
  Zap,
  Award,
  TrendingUp,
  ArrowRight,
  MapPin,
  Building
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';

const AboutPage = () => {
  const stats = [
    { value: '50K+', label: 'Active Users' },
    { value: '1M+', label: 'Posts Scheduled' },
    { value: '11', label: 'Platforms Supported' },
    { value: '99.9%', label: 'Uptime' }
  ];

  const values = [
    { icon: Heart, title: 'Customer First', description: 'Every decision starts with how it helps our customers succeed.' },
    { icon: Zap, title: 'Move Fast', description: 'We ship quickly, learn from feedback, and iterate constantly.' },
    { icon: Users, title: 'Team Over Ego', description: 'We celebrate wins together and support each other through challenges.' },
    { icon: Target, title: 'Focus on Impact', description: 'We prioritize work that creates meaningful value.' }
  ];

  const team = [
    { name: 'Sarah Chen', role: 'CEO & Co-Founder', avatar: 'SC', bio: 'Former Head of Product at Buffer' },
    { name: 'Michael Park', role: 'CTO & Co-Founder', avatar: 'MP', bio: 'Ex-Engineering Lead at Hootsuite' },
    { name: 'Emma Wilson', role: 'VP of Marketing', avatar: 'EW', bio: '10+ years in SaaS marketing' },
    { name: 'David Rodriguez', role: 'VP of Engineering', avatar: 'DR', bio: 'Built teams at Twitter and Meta' },
    { name: 'Lisa Thompson', role: 'Head of Customer Success', avatar: 'LT', bio: 'Passionate about user experience' },
    { name: 'Alex Kim', role: 'Head of Design', avatar: 'AK', bio: 'Design leader from Figma' }
  ];

  const timeline = [
    { year: '2023', title: 'Founded', description: 'StelliSocial started in a small apartment with a big vision.' },
    { year: '2024', title: 'Seed Funding', description: 'Raised $5M to build the future of social media management.' },
    { year: '2025', title: 'Product Launch', description: 'Launched to public with support for 5 platforms.' },
    { year: '2026', title: 'AI Integration', description: 'Added AI-powered content tools and video generation.' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/stellisocial-icon.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
              <span className="font-bold text-xl text-slate-900" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-500">Social</span></span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/features" className="text-slate-600 hover:text-slate-900">Features</Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900">Pricing</Link>
              <Link to="/blog" className="text-slate-600 hover:text-slate-900">Blog</Link>
              <Link to="/about" className="text-emerald-600 font-medium">About</Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600">Start Free Trial</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-emerald-100 text-emerald-700 mb-4">About Us</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Empowering Brands to
            <span className="text-emerald-500"> Connect & Grow</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
            We're building the tools that help businesses of all sizes succeed on social media.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-emerald-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <p className="text-4xl font-bold text-white">{stat.value}</p>
                <p className="text-emerald-100">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">Our Mission</h2>
          <p className="text-xl text-slate-600 leading-relaxed">
            We believe every business deserves access to powerful social media tools. 
            Our mission is to democratize social media marketing by providing an intuitive, 
            all-in-one platform that saves time, increases engagement, and drives real results.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Our Values</h2>
            <p className="text-slate-600">The principles that guide everything we do</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="text-center h-full border-slate-200">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                      <value.icon className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2">{value.title}</h3>
                    <p className="text-sm text-slate-600">{value.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Our Journey</h2>
            <p className="text-slate-600">From startup to industry leader</p>
          </div>
          <div className="space-y-8">
            {timeline.map((item, i) => (
              <motion.div
                key={item.year}
                initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex items-start gap-6"
              >
                <div className="w-20 flex-shrink-0">
                  <Badge className="bg-emerald-500 text-white text-lg px-3 py-1">{item.year}</Badge>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-lg">{item.title}</h3>
                  <p className="text-slate-600">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Meet Our Team</h2>
            <p className="text-slate-600">The people behind StelliSocial</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {team.map((member, i) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="border-slate-200">
                  <CardContent className="p-6 text-center">
                    <Avatar className="w-20 h-20 mx-auto mb-4">
                      <AvatarFallback className="bg-emerald-100 text-emerald-600 text-xl">{member.avatar}</AvatarFallback>
                    </Avatar>
                    <h3 className="font-semibold text-slate-900">{member.name}</h3>
                    <p className="text-emerald-600 text-sm mb-2">{member.role}</p>
                    <p className="text-slate-500 text-sm">{member.bio}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-emerald-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Join Our Journey</h2>
          <p className="text-emerald-100 mb-8">
            We're always looking for talented people to join our team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/careers">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-emerald-50">
                View Open Positions <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/register">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-emerald-600">
                Try StelliSocial Free
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/stellisocial-icon-square-transparent.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
                <span className="font-bold  text-white" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-400">Social</span></span>
              </div>
              <p className="text-sm">The complete social media management platform for teams.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/features" className="hover:text-white">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
                <li><Link to="/integrations" className="hover:text-white">Integrations</Link></li>
                <li><Link to="/developers" className="hover:text-white">API</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                <li><Link to="/help" className="hover:text-white">Help Center</Link></li>
                <li><Link to="/community" className="hover:text-white">Community</Link></li>
                <li><Link to="/webinars" className="hover:text-white">Webinars</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white">About</Link></li>
                <li><Link to="/careers" className="hover:text-white">Careers</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-sm text-center">
            &copy; {new Date().getFullYear()} StelliSocial. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
